import React from 'react';
import autobind from 'autobind-decorator';
import FrameMessages from '../const/FrameMessages';
import {getAdHostingWindow, getFrameId} from './utils';
import ClientAnalytics from '../analytics/ClientAnalytics';
import _ from 'lodash';
import urlParse from 'url-parse';
import {displayModes, renderModes} from '../const/AugmentationConst';
import IframeBottomBannerAd from './IframeBottomBannerAd';
import IframeModalAd from './IframeModalAd';
import {injectHtml} from '../utils/DomUtils';
import * as CampaignUtils from '../utils/CampaignUtils';
import * as BrowserUtils from '../utils/BrowserUtils';
import {AMAZON_AD_AFFILIATE_TYPE} from './const';
import {captureException} from '../utils/SentryUtils';

/**
 * [soon deprecated] Iframe Ads usin old AdTemplate configuration
 */
export default class IframeAdBase extends React.Component {
  constructor(props) {
    super(props);
    this.state = {};
  }

  componentDidMount() {
    if (this.isAmazonAdAffiliateType()) {
      this.setUpAmazonAdReload();
      if (this.isReloaded()) {
        this.trackReload();
      }
    } else {
      this.onAdLoaded();
    }
  }

  componentWillUnmount() {
    if (this.iframeClickInterval) {
      clearInterval(this.iframeClickInterval);
    }
  }

  isAmazonAdAffiliateType() {
    const {renderer} = this.props;
    return _.get(renderer, 'affiliateType.name') === AMAZON_AD_AFFILIATE_TYPE;
  }

  setUpAmazonAdReload() {
    // const amazonAdReloadTimeout = _.get(settings, 'widget.amazonAdReloadTimeout') || 1500;
    const amazonAdReloadTimeout = 4000;
    setTimeout(this.checkAmazonAdLoaded, amazonAdReloadTimeout);
  }

  @autobind
  hasAmazonContentLoaded() {
    const amznElem = document.querySelector('#vup-ad-content [id*="amzn"]');
    return amznElem && amznElem.children.length > 0;
  }

  @autobind
  checkAmazonAdLoaded() {
    if (!this.hasAmazonContentLoaded()) {
      // if (this.isReloaded()) {
      // hide after reloading once
      getAdHostingWindow().postMessage({title: FrameMessages.requestHide, frameId: this.getFrameId()}, '*');
      // } else {
      //   window.location.href = appendQuery(window.location.href, 'reloaded=1');
      // }
    } else {
      this.onAdLoaded();
    }
  }

  onAdLoaded() {
    this.trackImpression();
    this.applyTrackingCodes('load');
    this.setupAdClickTracking();
  }

  setupAdClickTracking() {
    const adContentElem = document.getElementById('vup-ad-content');
    if (adContentElem) {
      adContentElem.addEventListener('click', e => this.trackAdClick(e.target), true);
    }
    this.setupAdClickTrackingForIframes();
  }

  /***
   * Detection of clicks on internal iframes that are put in the Ad code (it doesn't work through regular onclick capturing)
   */
  setupAdClickTrackingForIframes() {
    const adContentElem = document.getElementById('vup-ad-content');

    if (!adContentElem) {
      return;
    }
    const contentIframes = adContentElem.getElementsByTagName('iframe');
    if (!contentIframes || !contentIframes.length) {
      return;
    }

    this.iframeClickInterval = setInterval(() => {
      const elem = document.activeElement;
      if (elem && elem.tagName === 'IFRAME') {
        elem.blur();
        this.trackAdClick();
      }
    }, 200);
  }

  @autobind
  trackAdClick(target) {
    const {renderer, campaignId, publisherId} = this.props;
    if (this.isAmazonAdAffiliateType()) {
      let url = null;
      let title = null;
      const productData = this.getClickedAmazonProductData(target);
      if (productData) {
        url = productData.url || null;
        title = productData.title || null;
      }
      ClientAnalytics.sendAdClicked(renderer.renderMode, renderer.id, campaignId, publisherId, url, title);
    } else {
      ClientAnalytics.sendAdClicked(renderer.renderMode, renderer.id, campaignId, publisherId, null, null);
    }
    this.applyTrackingCodes('click');
  }

  getClickedProductElem(clickTarget) {
    if (!clickTarget) return null;
    const adContentRootElem = document.getElementById('vup-ad-content');
    const productRootClass = 'amzn-native-product';

    if (clickTarget === adContentRootElem) {
      return null;
    }
    if (clickTarget.classList.contains(productRootClass)) {
      return clickTarget;
    }

    let parent = clickTarget;
    do {
      parent = parent.parentNode;
      if (parent && parent.classList.contains(productRootClass)) {
        return parent;
      }
    } while (parent && parent !== adContentRootElem);
  }

  getClickedAmazonProductData(clickTarget) {
    if (!clickTarget) {
      return null;
    }

    let url = null;
    let title = null;

    try {
      const productRootElem = this.getClickedProductElem(clickTarget);
      if (!productRootElem) {
        return null;
      }
      const titleElem = productRootElem.getElementsByClassName('amzn-native-product-title')[0];
      if (!titleElem) {
        return null;
      }
      url = titleElem.getAttribute('href');
      const titleTextElem = productRootElem.getElementsByClassName('amzn-native-product-title-text')[0];
      if (titleTextElem) {
        title = titleTextElem.textContent;
      }
      return {url, title};
    } catch (e) {
      captureException(e);
    }
  }

  isReloaded() {
    const parsedUrl = urlParse(window.location.href, true);
    return !!parsedUrl.query.reloaded;
  }

  @autobind
  trackImpression() {
    const {renderer, campaignId, publisherId} = this.props;
    ClientAnalytics.sendTriggerDisplayed(
      renderer.renderMode,
      displayModes.iframe,
      null,
      null,
      renderer.id,
      null,
      campaignId,
      publisherId,
      null);
  }

  @autobind
  trackReload() {
    const {renderer, campaignId} = this.props;
    ClientAnalytics.sendAdReloaded(
      renderer.renderMode,
      renderer.id,
      campaignId);
  }

  render() {
    const {renderer} = this.props;
    if (!renderer) {
      return null;
    }

    let ad = null;
    const adProps = {
      ...this.props,
      frameId: this.getFrameId(),
      closeTimeoutDuration: 0,
      hasAmazonContentLoaded: this.hasAmazonContentLoaded
    };

    switch (renderer.renderMode) {
      case renderModes.iframeVupopup: {
        ad = <IframeModalAd {...adProps} />;
        break;
      }
      case renderModes.iframeBottomBanner: {
        ad = <IframeBottomBannerAd {...adProps} />;
        break;
      }
      default:
        break;
    }

    return (
      <div className="vuPopupAdFrame__component">
        {ad}
        {this.renderTrackingCodeContainer()}
      </div>);
  }

  renderTrackingCodeContainer() {
    return (
      <span id="tracking-code"
            style={{position: 'absolute', bottom: '0', visibility: 'hidden'}}
            ref={(node) => {
              this.trackingCodeContainer = node;
            }}
      />
    );
  }

  applyTrackingCodes(eventType) {
    const {renderer} = this.props;
    let codes = '';
    if (!_.isEmpty(renderer.trackingCodes)) {
      const deviceType = BrowserUtils.getDeviceType();
      const fbEventId = this.getFbEventId(eventType);
      renderer.trackingCodes.forEach((codeConfig) => {
          const code = CampaignUtils.getRendererTrackingCode(codeConfig, deviceType, eventType, fbEventId);
          if (code) {
            codes += code;
          }
        }
      );
      if (codes) {
        const wrapper = document.createElement('span');
        this.trackingCodeContainer.appendChild(wrapper);
        injectHtml(wrapper, codes);
      }
    }
  }

  getFbEventId(eventType) {
    const {campaign, campaignId, renderer, publisherId, adTrackingId} = this.props;
    return CampaignUtils.getFbPixelEventId(
      campaign.userId,
      publisherId,
      adTrackingId || campaign.adTrackingId,
      campaignId,
      renderer.templateName,
      eventType
    );
  }

  getFrameId() {
    const {renderer, campaignId} = this.props;
    if (!renderer) {
      return null;
    }
    return getFrameId(renderer.id, null, campaignId);
  }
}
